import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { useAdminUserSession } from '../../../models/hooks/adminStateHooks';
import { invalidateBrowseAllSurveyTemplatesQuery } from '../useBrowseAllSurveyTemplatesQuery';
import { invalidateBrowseSurveyTemplatesQuery } from '../useBrowseSurveyTemplatesQuery';
import { invalidateSearchSurveyTemplatesQuery } from '../useSearchSurveyTemplatesQuery';
import { invalidateSurveyTemplatesByIndustryQuery } from '../useSurveyTemplatesByIndustryQuery';

export interface ICloneSurveyTemplateParams {
	id: string;
	name: string;
}

export interface ICloneSurveyTemplateOptions {
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: (data: Api.ISurveyTemplate) => void;
}

/**
 * Admin: Clone a survey template with a new name.
 * Creates a copy of the template that can be independently modified.
 *
 * @param options - Mutation options including success/error callbacks
 * @returns React Query mutation result
 */
export const useCloneSurveyTemplateMutation = (options: ICloneSurveyTemplateOptions = {}) => {
	const { onError, onSuccess } = options;
	const userSession = useAdminUserSession();

	return useMutation({
		mutationFn: (params: ICloneSurveyTemplateParams) => {
			const { id, name } = params;

			return userSession.webServiceHelper.callAsync<Api.ISurveyTemplate>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `admin/surveytemplate/${id}/clone`,
					queryParams: {
						name,
					},
				}),
				'POST'
			);
		},
		onSuccess: data => {
			// Refresh the template lists so the clone appears without a manual reload.
			invalidateSurveyTemplatesByIndustryQuery();
			invalidateBrowseAllSurveyTemplatesQuery();
			invalidateBrowseSurveyTemplatesQuery();
			invalidateSearchSurveyTemplatesQuery();
			onSuccess?.(data);
		},
		onError,
	});
};
